import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import LanguageFlag from '../../components/LanguageFlag';
import { ReminderService } from '../../services/ReminderService';

function Avatar({ name, email, size = 'lg' }) {
  const initials = (() => {
    const src = name || email || '?';
    const parts = src.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  })();

  const cls = size === 'lg'
    ? 'h-20 w-20 text-2xl'
    : 'h-10 w-10 text-sm';

  return (
    <div className={`${cls} flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 font-black text-white shadow-lg select-none`}>
      {initials}
    </div>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    logout,
    deleteAccount,
    usesPasswordProvider,
    languagePrefs,
    openLanguagePreferences,
  } = useAuth();
  const { languages } = useLanguages();
  const [remindersOn, setRemindersOn] = useState(() => ReminderService.isEnabled());
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-3xl bg-white border border-slate-200 shadow-xl p-8 text-center">
          <span className="text-5xl">🔒</span>
          <h2 className="mt-4 text-2xl font-black text-slate-900">{t('profile.loginRequired')}</h2>
          <p className="mt-2 text-sm text-slate-600">{t('profile.signInToView')}</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-3 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition"
          >
            {t('profile.loginArrow')}
          </button>
        </div>
      </div>
    );
  }

  const langMap = Object.fromEntries(languages.map((l) => [l.code, l]));
  const nativeLang = languagePrefs ? langMap[languagePrefs.nativeLanguage] : null;
  const targetLang = languagePrefs ? langMap[languagePrefs.targetLanguage] : null;

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  async function handleToggleReminders() {
    setReminderError('');
    setReminderBusy(true);
    if (remindersOn) {
      await ReminderService.disable();
      setRemindersOn(false);
    } else {
      const result = await ReminderService.enable();
      if (result.ok) {
        setRemindersOn(true);
      } else if (result.reason === 'permission_denied') {
        setReminderError(t('profile.remindersPermissionDenied'));
      } else {
        setReminderError(t('profile.remindersError'));
      }
    }
    setReminderBusy(false);
  }

  function openDeleteModal() {
    setDeletePassword('');
    setAccountError('');
    setShowDeleteModal(true);
  }

  function closeDeleteModal() {
    if (deleting) return;
    setShowDeleteModal(false);
  }

  async function confirmDeleteAccount() {
    if (usesPasswordProvider && !deletePassword) return;
    setDeleting(true);
    setAccountError('');
    const result = await deleteAccount({ password: deletePassword });
    setDeleting(false);
    if (result.ok) {
      navigate('/');
    } else {
      setShowDeleteModal(false);
      setAccountError(result.message || t('profile.deleteAccountFailed'));
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
      <div className="mx-auto max-w-lg space-y-5">

        {/* Back */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700 transition"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {t('profile.backToHome')}
        </button>

        {/* Profile card */}
        <div className="rounded-3xl bg-white border border-slate-200 shadow-xl overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-indigo-500 to-violet-600" />
          <div className="px-6 pb-6">
            <div className="-mt-10 mb-4">
              <Avatar name={user.name} email={user.email} size="lg" />
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black text-slate-900">
                {user.isAnonymous ? t('profile.guest') : (user.name || t('profile.learner'))}
              </h1>
              {user.isAnonymous && (
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">
                  {t('profile.guest')}
                </span>
              )}
            </div>
            {user.email && (
              <p className="mt-0.5 text-sm text-slate-500">{user.email}</p>
            )}
            {user.phone && (
              <p className="mt-0.5 text-sm text-slate-500">{user.phone}</p>
            )}
          </div>
        </div>

        {/* Guest upgrade prompt — anonymous accounts aren't recoverable if
            local browser storage is cleared, so nudge toward a real account
            before that progress is at risk. */}
        {user.isAnonymous && (
          <div className="rounded-3xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <p className="text-sm text-amber-800">{t('profile.guestUpgradeNotice')}</p>
            <button
              onClick={() => navigate('/register')}
              className="shrink-0 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition"
            >
              {t('profile.createAccount')}
            </button>
          </div>
        )}

        {/* Language preferences */}
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 space-y-4">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-400">{t('profile.languagePrefs')}</h2>

          {nativeLang && targetLang ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:w-auto sm:gap-3">
                <div className="flex min-w-0 flex-col items-center rounded-2xl border border-indigo-100 bg-indigo-50 px-2 py-3 sm:px-4">
                  <span className="flex w-full items-center justify-center gap-1.5 truncate text-center text-sm font-black text-indigo-700"><LanguageFlag flag={nativeLang.flag} label={nativeLang.label} />{nativeLang.nativeLabel || nativeLang.label}</span>
                  <p className="mt-1 text-[11px] font-bold text-indigo-700 text-center">{nativeLang.label}</p>
                  <p className="text-[9px] text-indigo-400 uppercase tracking-wider">{t('profile.iSpeak')}</p>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <svg className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider">{t('profile.learning')}</span>
                </div>
                <div className="flex min-w-0 flex-col items-center rounded-2xl border border-violet-100 bg-violet-50 px-2 py-3 sm:px-4">
                  <span className="flex w-full items-center justify-center gap-1.5 truncate text-center text-sm font-black text-violet-700"><LanguageFlag flag={targetLang.flag} label={targetLang.label} />{targetLang.nativeLabel || targetLang.label}</span>
                  <p className="mt-1 text-[11px] font-bold text-violet-700 text-center">{targetLang.label}</p>
                  <p className="text-[9px] text-violet-400 uppercase tracking-wider">{t('profile.goal')}</p>
                </div>
              </div>
              <button
                onClick={openLanguagePreferences}
                className="w-full rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 sm:w-auto"
              >
                {t('profile.change')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-slate-300 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">{t('profile.noLanguage')}</p>
              <button
                onClick={openLanguagePreferences}
                className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-xs font-bold text-white shadow-sm sm:w-auto"
              >
                {t('profile.choose')}
              </button>
            </div>
          )}
        </div>

        {/* Quick links */}
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 space-y-2">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-3">{t('profile.quickLinks')}</h2>
          {[
            { labelKey: 'progress.title', icon: '📈', to: '/progress', descKey: 'profile.progressDesc' },
            { labelKey: 'practice.title', icon: '🔥', to: '/practice', descKey: 'profile.practiceDesc' },
            { labelKey: 'tools.vocabulary', icon: '📚', to: '/learn/vocabulary', descKey: 'profile.vocabDesc' },
            { labelKey: 'conversation.title', icon: '🗣️', to: '/learn/conversations', descKey: 'profile.conversationsDesc' },
            { labelKey: 'games.title', icon: '🎮', to: '/games', descKey: 'profile.gamesDesc' },
            { labelKey: 'pronunciationGuide.title', icon: '👄', to: '/learn/pronunciation-guide', descKey: 'profile.pronunciationGuideDesc' },
            { labelKey: 'bookmarks.title', icon: '🔖', to: '/bookmarks', descKey: 'profile.bookmarksDesc' },
            { labelKey: 'search.title', icon: '🔎', to: '/search', descKey: 'profile.searchDesc' },
            { labelKey: 'practice.weakWords', icon: '💪', to: '/practice/weak-words', descKey: 'profile.weakWordsDesc' },
            { labelKey: 'leaderboard.title', icon: '🏆', to: '/leaderboard', descKey: 'profile.leaderboardDesc' },
            { labelKey: 'friends.title', icon: '👥', to: '/friends', descKey: 'profile.friendsDesc' },
            { labelKey: 'placement.title', icon: '🎯', to: '/placement-test', descKey: 'profile.placementDesc' },
            { labelKey: 'referral.title', icon: '🎁', to: '/invite', descKey: 'profile.referralDesc' },
          ].map((item) => (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              className="flex w-full items-center gap-3 rounded-2xl border border-slate-100 px-4 py-3 text-left hover:border-indigo-200 hover:bg-indigo-50 transition"
            >
              <span className="text-xl">{item.icon}</span>
              <span>
                <p className="text-sm font-bold text-slate-800">{t(item.labelKey)}</p>
                <p className="text-xs text-slate-500">{t(item.descKey)}</p>
              </span>
              <svg className="ml-auto h-4 w-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>

        {/* Preferences */}
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-3">{t('profile.preferences')}</h2>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-slate-800">{t('profile.dailyReminders')}</p>
              <p className="text-xs text-slate-500">{t('profile.dailyRemindersDesc')}</p>
            </div>
            <button
              onClick={handleToggleReminders}
              disabled={reminderBusy}
              role="switch"
              aria-checked={remindersOn}
              className={`relative h-7 w-12 shrink-0 rounded-full transition disabled:opacity-50 ${remindersOn ? 'bg-indigo-600' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${remindersOn ? 'left-6' : 'left-1'}`} />
            </button>
          </div>
          {reminderError && (
            <p role="alert" className="mt-2 text-xs font-semibold text-red-600">{reminderError}</p>
          )}
        </div>

        {/* Account actions */}
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-3">{t('profile.account')}</h2>
          <button
            onClick={handleLogout}
            className="w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-100 transition text-left"
          >
            {t('profile.signOut')}
          </button>
          <button
            onClick={openDeleteModal}
            disabled={deleting}
            className="mt-3 w-full rounded-2xl border border-red-300 px-4 py-3 text-sm font-bold text-red-700 hover:bg-red-50 disabled:cursor-wait disabled:opacity-60 transition text-left"
          >
            {deleting ? t('profile.deletingAccount') : t('profile.deleteAccount')}
          </button>
          {accountError && (
            <p role="alert" className="mt-3 text-sm font-semibold text-red-600">{accountError}</p>
          )}
        </div>

      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
          >
            <h2 id="delete-account-title" className="text-lg font-black text-slate-900">
              {t('profile.deleteAccount')}
            </h2>
            <p className="mt-2 text-sm text-slate-600">{t('profile.deleteAccountConfirm')}</p>

            {usesPasswordProvider && (
              <div className="mt-4">
                <label htmlFor="delete-account-password" className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">
                  {t('profile.passwordLabel')}
                </label>
                <input
                  id="delete-account-password"
                  type="password"
                  autoFocus
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder={t('profile.passwordPrompt')}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDeleteAccount}
                disabled={deleting || (usesPasswordProvider && !deletePassword)}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-wait disabled:opacity-50"
              >
                {deleting ? t('profile.deletingAccount') : t('profile.confirmDeleteButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
